/**
 * Utility functions for standardized error handling across the application
 */

/**
 * Standardizes error handling by formatting the error message and logging it
 *
 * @param error - The caught error
 * @param context - A string describing where the error occurred
 * @param additionalInfo - Optional additional information to include in the error message
 * @returns A standardized Error object
 */
export const handleApiError = (
  error: unknown,
  context: string,
  additionalInfo?: Record<string, unknown>
): Error => {
  // Format the error message
  const errorMessage = error instanceof Error ? error.message : String(error);

  // Create a standardized error object
  const standardError = error instanceof Error ? error : new Error(`${context}: ${errorMessage}`);

  // Log the error with context
  console.error(`Error in ${context}:`, error, additionalInfo || '');

  return standardError;
};

/**
 * Handles validation errors for required parameters
 *
 * @param paramName - The name of the parameter that is required
 * @param context - The context where the validation is happening
 * @returns A standardized Error object
 */
export const handleValidationError = (paramName: string, context: string): Error => {
  const errorMessage = `${paramName} is required`;
  console.error(`Validation error in ${context}: ${errorMessage}`);
  return new Error(errorMessage);
};
